import React, { useEffect, useMemo, useState } from "react";

/**
 * CSS-only balloon burst overlay — GPU-accelerated via transform+opacity.
 * Spawns N красных шариков that fly up and fade. No deps, no lag.
 *
 * Usage: render once per click event with a key to force remount.
 *
 *   const [burstKey, setBurstKey] = useState(0);
 *   <button onClick={() => setBurstKey(k => k + 1)}>...</button>
 *   {burstKey > 0 && <BalloonBurst key={burstKey} />}
 */

const BALLOON_COUNT = 22;
const DURATION_MS = 1800;

function makeBalloon(i, total) {
  // Angle: spread uniformly in top half, with jitter
  const baseAngle = -90 + (i / total - 0.5) * 160;  // -90 = straight up
  const jitter = (Math.random() - 0.5) * 20;
  const angle = baseAngle + jitter;
  const distance = 180 + Math.random() * 220;  // px travel
  const rad = (angle * Math.PI) / 180;
  const dx = Math.cos(rad) * distance;
  const dy = Math.sin(rad) * distance - 40; // slight extra up
  const size = 22 + Math.round(Math.random() * 20);
  const rotate = (Math.random() - 0.5) * 420;
  const delay = Math.random() * 150;
  const hueShift = Math.round((Math.random() - 0.5) * 28);
  return { id: i, dx, dy, size, rotate, delay, hueShift };
}

export default function BalloonBurst({ color = "#ef4444", count = BALLOON_COUNT }) {
  const balloons = useMemo(
    () => Array.from({ length: count }, (_, i) => makeBalloon(i, count)),
    [count]
  );
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDone(true), DURATION_MS + 400);
    return () => clearTimeout(t);
  }, []);

  if (done) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "visible",
        zIndex: 50,
      }}
    >
      <style>{`
        @keyframes balloon-fly {
          0%   { transform: translate(-50%, -50%) scale(0.2) rotate(0deg); opacity: 0; }
          12%  { transform: translate(-50%, -50%) scale(1) rotate(0deg); opacity: 1; }
          100% { transform:
                   translate(calc(-50% + var(--bx)), calc(-50% + var(--by)))
                   scale(0.95) rotate(var(--br));
                 opacity: 0; }
        }
      `}</style>
      {balloons.map((b) => {
        const w = b.size;
        const h = Math.round(w * 1.2);
        const stringH = Math.round(h * 1.1);
        return (
          <div
            key={b.id}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: w,
              height: h + stringH,
              transform: "translate(-50%, -50%)",
              willChange: "transform, opacity",
              animation: `balloon-fly ${DURATION_MS}ms cubic-bezier(0.22, 0.6, 0.2, 1) ${b.delay}ms forwards`,
              ["--bx"]: `${b.dx}px`,
              ["--by"]: `${b.dy}px`,
              ["--br"]: `${b.rotate}deg`,
              filter: `hue-rotate(${b.hueShift}deg) drop-shadow(0 2px 4px rgba(0,0,0,0.3))`,
            }}
          >
            {/* Balloon body */}
            <div
              style={{
                width: w,
                height: h,
                borderRadius: "50% 50% 48% 48% / 55% 55% 45% 45%",
                background: `radial-gradient(circle at 32% 28%, #ffb3b3 0%, ${color} 55%, #991b1b 100%)`,
                position: "relative",
              }}
            >
              {/* Highlight */}
              <div
                style={{
                  position: "absolute",
                  top: "15%",
                  left: "22%",
                  width: "22%",
                  height: "22%",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.65)",
                  filter: "blur(2px)",
                }}
              />
              {/* Knot */}
              <div
                style={{
                  position: "absolute",
                  bottom: -4,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#7f1d1d",
                }}
              />
            </div>
            {/* String */}
            <svg
              width={w}
              height={stringH}
              style={{ position: "absolute", top: h, left: 0, pointerEvents: "none" }}
            >
              <path
                d={`M ${w / 2} 0 Q ${w / 2 + 6} ${stringH / 2}, ${w / 2 - 2} ${stringH}`}
                stroke="rgba(0,0,0,0.4)"
                strokeWidth="1"
                fill="none"
              />
            </svg>
          </div>
        );
      })}
    </div>
  );
}
